import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, Linkedin, MapPin, Building2, Briefcase, Sparkles, ListChecks, RefreshCw, Loader2, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { getRegionLabel } from '../context/RegionContext';
import { TrustRing, VerdictBadge, verdictKey } from '../components/scout/Verdict';
import SignalCard from '../components/scout/SignalCard';
import OverridePanel from '../components/scout/OverridePanel';
import { getScoutScan, rescanScoutScan, apiError } from '../services/scoutApi';

const Chip = ({ icon: Icon, children, testid }) => (children ? (
  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/70 px-2.5 py-1 rounded-full max-w-full truncate" data-testid={testid}>
    <Icon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{children}</span>
  </span>
) : null);

const Scanning = ({ name }) => (
  <div className="rounded-2xl border bg-card p-12 flex flex-col items-center text-center gap-4" data-testid="scout-scanning-state">
    <span className="relative w-20 h-20 rounded-full border-2 border-primary/30 flex items-center justify-center">
      <span className="absolute inset-0 rounded-full border-2 border-primary animate-ping opacity-40" />
      <Loader2 className="h-8 w-8 text-primary animate-spin" />
    </span>
    <div>
      <p className="font-semibold">Scout is checking {name}</p>
      <p className="text-sm text-muted-foreground mt-1">Email, phone, LinkedIn, resume, duplicates, GitHub, history and location — usually under a minute.</p>
    </div>
  </div>
);

export default function ScoutReportPage() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ['admin', 'recruiter'].includes(user?.role);
  const [scan, setScan] = useState(null);
  const [rescanning, setRescanning] = useState(false);

  const load = useCallback(async () => {
    try { setScan(await getScoutScan(scanId)); } catch (e) { toast.error(apiError(e, 'Scan not found'), { id: 'scout-report-error' }); navigate('/scout'); }
  }, [scanId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (scan?.status !== 'running') return undefined;
    const id = setTimeout(load, 3000);
    return () => clearTimeout(id);
  }, [scan, load]);

  const rescan = async () => {
    setRescanning(true);
    try {
      const r = await rescanScoutScan(scan.id);
      navigate(`/scout/${r.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setRescanning(false); }
  };

  if (!scan) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const inp = scan.input || {};
  const v = verdictKey(scan);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="scout-report-page">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate('/scout')} data-testid="scout-back-btn"><ArrowLeft className="h-4 w-4" /> Scout</Button>
        {canManage && scan.status !== 'running' && (
          <Button variant="outline" size="sm" className="gap-2" disabled={rescanning} onClick={rescan} data-testid="scout-rescan-btn">
            {rescanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-scan
          </Button>
        )}
      </div>

      <section className="rounded-2xl border bg-card p-6 flex flex-col md:flex-row md:items-center gap-6">
        <TrustRing score={scan.trust_score} verdict={v} testid="scout-trust-ring" />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="scout-report-name">{inp.name}</h1>
            <VerdictBadge verdict={v} size="lg" testid="scout-report-verdict" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Chip icon={Mail} testid="scout-chip-email">{inp.email || scan.identity?.email}</Chip>
            <Chip icon={Phone} testid="scout-chip-phone">{inp.phone || scan.identity?.phone}</Chip>
            <Chip icon={Linkedin} testid="scout-chip-linkedin">{inp.linkedin_url || scan.identity?.linkedin}</Chip>
            <Chip icon={MapPin}>{inp.location}</Chip>
            <Chip icon={Building2}>{scan.vendor}</Chip>
            <Chip icon={Briefcase}>{scan.job_title}</Chip>
          </div>
          <p className="text-xs text-muted-foreground">
            {getRegionLabel(scan.region)} · scanned {new Date(scan.completed_at || scan.created_at).toLocaleString()} by {scan.created_by === 'auto' ? 'auto-scan' : scan.created_by}
          </p>
        </div>
      </section>

      {scan.status === 'running' && <Scanning name={inp.name} />}
      {scan.status === 'failed' && <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 text-sm" data-testid="scout-failed-state">Scan failed: {scan.error}. Use Re-scan to try again.</div>}

      {scan.status === 'completed' && (
        <>
          <OverridePanel scan={scan} canManage={canManage} onSaved={load} />
          <section className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
            <div className="rounded-2xl border bg-card p-5" data-testid="scout-ai-summary">
              <p className="text-[11px] font-bold uppercase tracking-wider text-primary flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Scout summary</p>
              <p className="text-sm leading-relaxed mt-2">{scan.ai?.summary}</p>
            </div>
            <div className="rounded-2xl border bg-card p-5" data-testid="scout-next-steps">
              <p className="text-[11px] font-bold uppercase tracking-wider text-primary flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Recommended next steps</p>
              <ol className="mt-2 space-y-1.5 list-decimal list-inside text-sm">{(scan.ai?.next_steps || []).map((s) => <li key={s}>{s}</li>)}</ol>
            </div>
          </section>
          <section className="grid md:grid-cols-2 gap-4" data-testid="scout-signals-grid">
            {(scan.signals || []).map((s, i) => <SignalCard key={s.key} signal={s} index={i} />)}
          </section>
        </>
      )}

      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5" data-testid="scout-report-disclaimer">
        <Scale className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        Scout uses details supplied by the candidate and public account checks. It is not a background check or consumer report — never reject a candidate on these signals alone; verify them first.
      </p>
    </div>
  );
}
